import type {
  AndroidPushMessageGet,
  AndroidPushMessagePost,
  IosPushMessageGet,
  IosPushMessagePost,
  KindlePushMessageGet,
  KindlePushMessagePost,
  MessageGet,
  MessagePost,
  WebPushMessageGet,
  WebPushMessagePost,
} from "@schemas/types";

import type { ContentOptimizationErrorTextKey } from "./types";

type PushMessageData =
  | AndroidPushMessageGet
  | AndroidPushMessagePost
  | IosPushMessageGet
  | IosPushMessagePost
  | KindlePushMessageGet
  | KindlePushMessagePost
  | WebPushMessageGet
  | WebPushMessagePost;

const PUSH_MESSAGE_TYPES = new Set(["androidPush", "iosPush", "kindlePush", "webPush"]);

export const isPushMessage = (msg: MessageGet | MessagePost): msg is PushMessageData => {
  return PUSH_MESSAGE_TYPES.has(msg.message_type);
};

/**
 * Extracts Liquid-capable fields from a push message for validation.
 * All push content is collected under the "generic" error text key.
 *
 * @param messageData - The push message data (GET or POST shape, any platform)
 * @param includeImageUrls - Whether to include image and icon URL fields
 * @returns An object mapping the "generic" error text key to arrays of text values
 */
export const extractPushMessageTexts = (
  messageData: PushMessageData,
  { includeImageUrls = false }: { includeImageUrls?: boolean } = {}
): Partial<Record<ContentOptimizationErrorTextKey, string[]>> => {
  const texts: string[] = [];

  switch (messageData.message_type) {
    case "androidPush": {
      if (messageData.android_title) {
        texts.push(messageData.android_title);
      }
      if (messageData.android_push_message) {
        texts.push(messageData.android_push_message);
      }
      if (includeImageUrls && messageData.image_url) {
        texts.push(messageData.image_url);
      }
      if (includeImageUrls && messageData.android_push_icon_image_url) {
        texts.push(messageData.android_push_icon_image_url);
      }
      break;
    }
    case "iosPush": {
      if (messageData.ios_alert_hash?.title) {
        texts.push(messageData.ios_alert_hash.title);
      }
      if (messageData.ios_push_message) {
        texts.push(messageData.ios_push_message);
      }
      if (includeImageUrls && messageData.ios_image_url) {
        texts.push(messageData.ios_image_url);
      }
      break;
    }
    case "webPush": {
      if (messageData.web_title) {
        texts.push(messageData.web_title);
      }
      if (messageData.web_push_message) {
        texts.push(messageData.web_push_message);
      }
      if (includeImageUrls && messageData.large_image_url) {
        texts.push(messageData.large_image_url);
      }
      if (includeImageUrls && messageData.image_url) {
        texts.push(messageData.image_url);
      }
      break;
    }
    case "kindlePush": {
      if (messageData.kindle_title) {
        texts.push(messageData.kindle_title);
      }
      if (messageData.kindle_push_message) {
        texts.push(messageData.kindle_push_message);
      }
      break;
    }
  }

  if (texts.length === 0) {
    return {};
  }

  return { generic: texts };
};
